import React, { useState } from 'react';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { useSelectOptions, useHandleSearch } from 'hooks/form/useSelectOptions';

/**
 * CustomSelect component to handle select input with search functionality.
 * @param {object} props - The properties for the component.
 * @param {string} props.url - The base URL for the API endpoint.
 * @param {string} props.searchQuery - The search query for filtering options.
 * @param {object} props.value - The current value of the select.
 * @param {function} props.onChange - Callback for when the value changes.
 * @param {boolean} [props.isCreatable=false] - Whether the select is creatable.
 * @param {function} [props.formatOptionLabel] - Function to format option labels.
 * @param {string} [props.valueKey='id'] - Key to use for option values.
 * @param {string} [props.labelKey='name'] - Key to use for option labels.
 * @param {string} [props.placeholder='Select...'] - Placeholder text for the select.
 * @returns {JSX.Element} - The rendered select component.
 */
const CustomSelect = ({
    url,
    value,
    onChange,
    isCreatable = false,
    formatOptionLabel,
    valueKey = 'id',
    labelKey = 'name',
    placeholder = 'Select...',
    ...props
}) => {
    const { searchQuery, reload, handleSearchQueryChange } = useHandleSearch();
    const { options, isLoading } = useSelectOptions(url, searchQuery, valueKey, labelKey, reload);

    const handleChange = (selectedOption) => {
        onChange(selectedOption);
    };

    const SelectComponent = isCreatable ? Creatable : Select;

    return (
        <SelectComponent
            value={value}
            onChange={handleChange}
            onInputChange={handleSearchQueryChange}
            options={options}
            isLoading={isLoading}
            formatOptionLabel={formatOptionLabel}
            placeholder={placeholder}
            {...props}
        />
    );
};

export default CustomSelect;
